/**
 * The template editor's print guide finish: which original AI image a view came from, and the
 * case image the editor keys once the view is flattened or its camera shading fixed
 * (see `editor/cameraShading.ts`).
 */
import { useEffect, useRef, useState } from "react";
import type { GenerationJobView } from "@genz/platform";
import type { AppLogger } from "../app/context.js";
import { changesGuide, finishedUrl, type GuideFinish } from "../editor/cameraShading.js";

/** A finished cloud job's own magenta image keeps the shading the composite flattened. */
const hasOriginal = (j: GenerationJobView) => Boolean(j.originalUrl);

/**
 * The job whose original AI image is behind `view`: a job this editor applied to that view,
 * else, on the template's main view (the one the Generate wizard fills), the newest job published
 * as it. Its `originalUrl` is a link that expires, so the caller reads the job again to use it.
 */
export function shadingSourceJob(
  listed: GenerationJobView[],
  session: { view: string; job: GenerationJobView }[],
  templateId: string,
  view: string,
  mainView: string,
): string | null {
  const applied = [...session].reverse().find((e) => e.view === view && hasOriginal(e.job));
  if (applied) return applied.job.id;
  if (view !== mainView) return null;
  return listed.find((j) => j.publishedTemplateId === templateId && j.view === view && hasOriginal(j))?.id ?? null;
}

/**
 * `url` with the view's `finish` applied, or `url` itself when it changes nothing. While a new
 * image is being made the last one ready stays on screen, so the editor reloads once and is never
 * blank; `pending` says the image shown is not yet the one `finish` asks for. A failure keeps the
 * image as generated.
 */
export function useFinishedCaseUrl(
  url: string | undefined,
  finish: GuideFinish | null | undefined,
  logger?: AppLogger,
): { url: string | undefined; pending: boolean } {
  const key = url && changesGuide(finish)
    ? JSON.stringify([url, finish?.shading_fix ?? null, Boolean(finish?.flatten_magenta)])
    : null;
  const [ready, setReady] = useState<{ key: string; url: string } | null>(null);
  const objectUrl = useRef<string | null>(null);
  const release = () => {
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    objectUrl.current = null;
  };
  useEffect(() => release, []);
  useEffect(() => {
    if (!key || !url || !finish) {
      release();
      setReady(null);
      return;
    }
    let cancelled = false;
    void finishedUrl(url, finish).then(
      (u) => {
        if (cancelled) return URL.revokeObjectURL(u);
        release();
        objectUrl.current = u;
        setReady({ key, url: u });
      },
      (e: unknown) => {
        logger?.log("error", "Editor · the print guide could not be finished — keying it as generated", { error: e });
        if (!cancelled) setReady({ key, url });
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` carries url and finish by value.
  }, [key]);
  if (!key) return { url, pending: false };
  return ready?.key === key ? { url: ready.url, pending: false } : { url: ready?.url ?? url, pending: true };
}
